import { AppDialog } from "@/components/app-dialog";
import { modKey } from "@/lib/format";
import { useProjectStore } from "@/store/project-store";
import { isDesktop } from "@/desktop/runtime";
import { makeStyles, tokens } from "@fluentui/react-components";

const DESKTOP_SECTION: { title: string; rows: Array<[string, string]> } = {
  title: "데스크탑",
  rows: [
    [`${modKey}${modKey === "⌘" ? "⌥" : "Alt+"}L`, "로그 보기"],
    [`${modKey}${modKey === "⌘" ? "⌥" : "Alt+"}M`, "MCP 연결"],
  ],
};

const SECTIONS: Array<{ title: string; rows: Array<[string, string]> }> = [
  {
    title: "편집",
    rows: [
      ["↩", "셀 편집 · 편집 중 확정"],
      ["esc", "편집 취소 · 선택 해제"],
      [`${modKey}↩`, "아래에 작업 추가"],
      [`⇧${modKey}↩`, "하위 작업 추가"],
      ["⇥ / ⇧⇥", "들여쓰기 / 내어쓰기"],
      ["⌥↑ / ⌥↓", "위로 / 아래로 이동"],
      [`${modKey}⌫`, "선택 작업 삭제"],
      [`${modKey}Z / ⇧${modKey}Z`, "실행 취소 / 다시 실행"],
      ["우클릭", "행·바 컨텍스트 메뉴"],
    ],
  },
  {
    title: "이동·선택",
    rows: [
      ["↑ ↓", "행 이동 · ⇧와 함께 범위 선택"],
      ["← →", "셀 이동 · 요약 작업에서는 접기/펼치기"],
      ["space", "요약 작업 접기/펼치기"],
      [`${modKey}A`, "모두 선택"],
      [`${modKey}클릭`, "개별 추가 선택"],
      [`${modKey}K`, "명령 팔레트"],
    ],
  },
  {
    title: "보기",
    rows: [
      [`${modKey}+ / ${modKey}-`, "확대 / 축소"],
      [`${modKey}0`, "플랜·선택 맞춤"],
      [`${modKey}1 / 2 / 3`, "일 / 주 / 월"],
      [`${modKey}T`, "오늘로 이동"],
      [`${modKey}B`, "탐색 사이드바"],
      [`${modKey}I`, "사이드 패널"],
      [`${modKey},`, "앱 설정"],
      [`${modKey}/`, "이 도움말"],
    ],
  },
  {
    title: "작업 · 보드 · 캘린더",
    rows: [
      [`${modKey}E`, "새 Task (상세 열림)"],
      [`${modKey}Z / ⇧${modKey}Z`, "Task 편집도 되돌리기"],
      [`${modKey}⌫`, "열린/선택한 Task 삭제"],
      [`${modKey}클릭`, "목록에서 여러 Task 선택 → 일괄 편집"],
      ["카드 드래그", "보드 열 이동 · 캘린더 날짜 변경"],
      ["미정 트레이 드래그", "날짜 셀에 놓아 마감 지정"],
      ["WBS 칩 클릭", "일정 탭에서 해당 WBS·작업 열기"],
    ],
  },
  {
    title: "간트 마우스",
    rows: [
      [`${modKey}+휠`, "커서 기준 가로 줌"],
      ["바 드래그", "시작 제약(SNET)으로 이동 · 선행 앞으로는 불가"],
      ["오른쪽 끝 드래그", "기간 변경"],
      ["끝점 ● 드래그", "다른 행으로 놓아 FS 관계 연결"],
      ["바 위 hover", "날짜·기간·여유·진척"],
      ["스플리터 드래그", "열 끝에 붙여 표와 간트 너비"],
      ["스플리터 더블클릭", "작업 열만 보기 ↔ 켜 둔 열 전체"],
      ["작업 열 헤더 더블클릭", "가장 긴 이름에 맞추기"],
    ],
  },
];

const useStyles = makeStyles({
  group: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
    marginBottom: tokens.spacingVerticalS,
    ":last-child": { marginBottom: 0 },
  },
  title: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
  },
  body: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    padding: `2px ${tokens.spacingHorizontalS}`,
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} 0`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ":last-child": { borderBottom: "none" },
  },
  label: {
    minWidth: 0,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1,
  },
  key: {
    flexShrink: 0,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: "tabular-nums",
  },
});

export function ShortcutsBody() {
  const styles = useStyles();
  return (
    <>
      {(isDesktop() ? [...SECTIONS, DESKTOP_SECTION] : SECTIONS).map((sec) => (
        <section key={sec.title} className={styles.group}>
          <div className={styles.title}>{sec.title}</div>
          <div className={styles.body}>
            {sec.rows.map(([k, v]) => (
              <div key={k} className={styles.row}>
                <span className={styles.label}>{v}</span>
                <span className={styles.key}>{k}</span>
              </div>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

export function ShortcutsDialog() {
  const open = useProjectStore((s) => s.dialog === "shortcuts");
  const close = () => useProjectStore.getState().openDialog(null);
  return (
    <AppDialog open={open} onClose={close} title="단축키" description="시작·종료일은 계산 결과입니다. 기간·선행·제약을 바꾸면 날짜가 따라옵니다." width="lg">
      <ShortcutsBody />
    </AppDialog>
  );
}
